import { ReactNode, useState } from "react"
import { Box, Collapse, Skeleton, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { useInView } from "react-intersection-observer"

import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { DeliveryType, MeetingServiceOrder } from "../../meetingServiceTypes"
import { SimpleStatusChip } from "../../components/StatusMenu"
import { MeetingServiceOrdersDependencies } from "../../components/OrderDeliveryList"
import { LOCATION_UNKNOWN, ProductionDateSection, ProductionGroupBy, ProductionLayout } from "../helpers"
import { ProductionOrderCard } from "./ProductionOrderCard"

const useStyles = makeStyles()((theme) => ({
    body: {
        flex: 1,
        padding: "1.5rem 0",
    },
    content: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        paddingBottom: "4rem",
    },
    dateSection: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    group: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
    },
    statusGroupChip: {
        alignSelf: "flex-start",
    },
    hourHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: ".5rem",
        height: "4rem",
        padding: ".75rem 1rem",
        borderRadius: ".625rem",
        background: theme.palette.tailwind.blue[200],
        cursor: "pointer",
    },
    hourHeaderLeft: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".75rem",
    },
    hourChevron: {
        color: theme.palette.tailwind.slate[700],
        fontSize: "1.25rem",
        width: "1.25rem",
        height: "1.25rem",
    },
    hourLabel: {
        fontWeight: 700,
        fontSize: "1.25rem",
        lineHeight: 1.6,
        letterSpacing: ".009rem",
        color: theme.palette.tailwind.blue[900],
    },
    ordersCount: {
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: ".009rem",
        color: theme.palette.tailwind.slate[800],
    },
    cards: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    empty: {
        padding: "4rem 1rem",
        textAlign: "center",
        color: theme.palette.tailwind.slate[500],
    },
    skeleton: {
        borderRadius: ".75rem",
    },
}))

type Props = Readonly<{
    isInitialLoading: boolean
    isEmpty: boolean
    layout: ProductionLayout
    selectedOrderIds: number[]
    onToggleSelect: (orderId: number) => void
    onEditOrder: (orderId: number) => void
    onDeleteOrder: (order: MeetingServiceOrder) => void
    onOrderSaved: () => void
    meetingServiceDependencies: MeetingServiceOrdersDependencies
}>

const LOCATION_DELIVERY_TYPES: ReadonlyArray<string> = [DeliveryType.EATING_IN_CANTEEN, DeliveryType.PICK_UP]

// LazyCard defers a card's mount until it scrolls near the viewport
const useLazyCardStyles = makeStyles()(() => ({
    placeholder: {
        minHeight: "9rem",
    },
}))

function LazyCard({ children }: Readonly<{ children: ReactNode }>) {
    const { classes, cx } = useLazyCardStyles()
    const { ref, inView } = useInView({ triggerOnce: true, rootMargin: "800px 0px" })
    return (
        <div ref={ref} className={cx(!inView && classes.placeholder)}>
            {inView ? children : null}
        </div>
    )
}

export function ProductionViewBody({
    isInitialLoading,
    isEmpty,
    layout,
    selectedOrderIds,
    onToggleSelect,
    onEditOrder,
    onDeleteOrder,
    onOrderSaved,
    meetingServiceDependencies,
}: Props) {
    const { classes } = useStyles()
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

    const toggleGroup = (key: string) =>
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    const renderCard = (order: MeetingServiceOrder) => (
        <LazyCard key={order.id}>
            <ProductionOrderCard
                order={order}
                onEdit={(target) => onEditOrder(target.id)}
                onDelete={onDeleteOrder}
                onSaved={onOrderSaved}
                isSelected={selectedOrderIds.includes(order.id)}
                onToggleSelect={() => onToggleSelect(order.id)}
                meetingServiceDependencies={meetingServiceDependencies}
            />
        </LazyCard>
    )

    // collapsible group: the blue hour-bar look, reused for both time and location grouping.
    const renderCollapsibleGroup = (key: string, label: ReactNode, orders: ReadonlyArray<MeetingServiceOrder>) => {
        const isCollapsed = collapsedGroups.has(key)
        return (
            <Box key={key} className={classes.group}>
                <Box className={classes.hourHeader} onClick={() => toggleGroup(key)}>
                    <Box className={classes.hourHeaderLeft}>
                        <FontAwesomeIcon
                            icon={isCollapsed ? faChevronDown : faChevronUp}
                            className={classes.hourChevron}
                        />
                        {label}
                    </Box>
                    <LocalizedStrict
                        id="meeting-service-production-view-hour-group-orders-count"
                        vars={{ count: orders.length }}
                    >
                        <Typography component="span" className={classes.ordersCount}>
                            {orders.length} orders
                        </Typography>
                    </LocalizedStrict>
                </Box>
                <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                    <Box className={classes.cards}>{orders.map(renderCard)}</Box>
                </Collapse>
            </Box>
        )
    }

    const renderHourGroups = (section: ProductionDateSection) =>
        section.hourGroups.map((hourGroup) =>
            renderCollapsibleGroup(
                hourGroup.key,
                <Typography component="span" className={classes.hourLabel}>
                    {hourGroup.timeLabel}
                </Typography>,
                hourGroup.orders,
            ),
        )

    const renderLocationLabel = (key: string) => {
        if (LOCATION_DELIVERY_TYPES.includes(key)) {
            return (
                <LocalizedEnum base="delivery-type-filter" enumValue={key}>
                    <Typography component="span" className={classes.hourLabel}>
                        {key}
                    </Typography>
                </LocalizedEnum>
            )
        }
        if (key === LOCATION_UNKNOWN) {
            return (
                <LocalizedStrict id="meeting-service-production-view-location-unknown">
                    <Typography component="span" className={classes.hourLabel}>
                        Unknown location
                    </Typography>
                </LocalizedStrict>
            )
        }
        return (
            <Typography component="span" className={classes.hourLabel}>
                {key}
            </Typography>
        )
    }

    return (
        <Box className={classes.body}>
            <Box className={classes.content}>
                {isInitialLoading &&
                    Array.from({ length: 4 }).map((_, index) => (
                        <Skeleton key={index} variant="rectangular" height="9rem" className={classes.skeleton} />
                    ))}

                {isEmpty && (
                    <LocalizedStrict id="meeting-service-production-view-empty">
                        <Typography variant="body1" className={classes.empty}>
                            No orders to produce
                        </Typography>
                    </LocalizedStrict>
                )}

                {!isInitialLoading &&
                    layout.mode === ProductionGroupBy.TIME &&
                    layout.sections.map((section) => (
                        <Box key={section.dateKey} className={classes.dateSection}>
                            {renderHourGroups(section)}
                        </Box>
                    ))}

                {!isInitialLoading &&
                    layout.mode === ProductionGroupBy.LOCATION &&
                    layout.locationSections.map((section) => (
                        <Box key={section.dateKey} className={classes.dateSection}>
                            {section.locationGroups.map((group) =>
                                renderCollapsibleGroup(group.key, renderLocationLabel(group.key), group.orders),
                            )}
                        </Box>
                    ))}

                {!isInitialLoading &&
                    layout.mode === ProductionGroupBy.STATUS &&
                    layout.statusGroups.map((group) => (
                        <Box key={group.status} className={classes.group}>
                            <SimpleStatusChip
                                status={group.status}
                                tooltipLabel=""
                                className={classes.statusGroupChip}
                            />
                            <Box className={classes.cards}>{group.orders.map(renderCard)}</Box>
                        </Box>
                    ))}

                {!isInitialLoading &&
                    layout.mode === ProductionGroupBy.DATE &&
                    layout.dateGroups.map((group) =>
                        renderCollapsibleGroup(
                            group.key,
                            <Typography component="span" className={classes.hourLabel}>
                                {group.dateLabel}
                            </Typography>,
                            group.orders,
                        ),
                    )}
            </Box>
        </Box>
    )
}
